import Fuse from "fuse.js";
import { Sort } from "component/shared/table";
import { sortComparator } from "shared/sort-comparator";
import { Lead } from "model/lead";
import { User } from "model/user";
import { LeadColumnType } from "type/lead-column";
import * as env from "shared/env";
import { hasSearchAgent } from "shared/search-agent-permission";

const fuseOptions: Fuse.IFuseOptions<Lead> = {
	shouldSort: true,
	includeScore: true,
	threshold: 0.1,
	keys: ["name", "email", "email2", "phone"],
};

export const searchLeads = (leads: Lead[], term: string) => {
	if (!term) {
		return leads;
	}
	return new Fuse(leads, fuseOptions).search(term).map((result) => result.item);
};

export const getLeadsPageColumnTypes = (user: User, savedColumnTypes: LeadColumnType[]) => {
	const columnTypes = user.permissions.crm ? [...savedColumnTypes] : [];
	if (!columnTypes.length) {
		columnTypes.push(LeadColumnType.AVATAR);
		columnTypes.push(LeadColumnType.LABEL);
		if (!user.permissions.crm) {
			columnTypes.push(LeadColumnType.STATUS);
		}
		if (!user.permissions.crm) {
			columnTypes.push(LeadColumnType.CREATED_ON);
		}
		if (user.permissions.crm) {
			columnTypes.push(LeadColumnType.LAST_ACTIVE_ON);
		}
		if (user.permissions.crm && hasSearchAgent(user)) {
			columnTypes.push(LeadColumnType.LAST_SEARCH_AGENT_CHAT_ON);
		}
		columnTypes.push(LeadColumnType.SUBSCRIBED);
		if (user.permissions.crmPipeline) {
			columnTypes.push(LeadColumnType.PIPELINE);
		}
		if (user.permissions.crmTags) {
			columnTypes.push(LeadColumnType.TYPE);
		}
		if (user.permissions.crm && env.leadRanking) {
			columnTypes.push(LeadColumnType.RANKING);
		}
		columnTypes.push(LeadColumnType.SOURCE);
		if (user.permissions.crmTags) {
			columnTypes.push(LeadColumnType.BOOKMARKED);
		}
		if (user.permissions.crmTags) {
			columnTypes.push(LeadColumnType.TAGS);
		}
		if (user.permissions.broker && user.permissions.showTeamFeatures) {
			columnTypes.push(LeadColumnType.AGENT);
		}
	}
	return columnTypes;
};

interface LeadSortColumn {
	columnType: LeadColumnType;
	permitted?: (user: User) => boolean;
	compare: (lead1: Lead, lead2: Lead) => number;
	isBlank?: (lead: Lead) => boolean;
}

// keyed by the Lead List table column id
export const leadSortColumns: Record<string, LeadSortColumn> = {
	label: {
		columnType: LeadColumnType.LABEL,
		compare: (lead1, lead2) => {
			const leadOne = lead1.label.toLowerCase();
			const leadTwo = lead2.label.toLowerCase();
			return leadOne < leadTwo ? -1 : leadOne > leadTwo ? 1 : 0;
		},
	},
	status: {
		columnType: LeadColumnType.STATUS,
		compare: (lead1, lead2) => lead1.status.label.localeCompare(lead2.status.label),
	},
	pipeline: {
		columnType: LeadColumnType.PIPELINE,
		permitted: (user) => !!user.permissions.crmPipeline,
		compare: (lead1, lead2) => {
			const pipeline1 = lead1.pipeline;
			const pipeline2 = lead2.pipeline;
			if (!pipeline1 && !pipeline2) return 0;
			if (!pipeline1) return 1;
			if (!pipeline2) return -1;
			return pipeline1.type.label < pipeline2.type.label ? -1 : 1;
		},
	},
	createdOn: {
		columnType: LeadColumnType.CREATED_ON,
		compare: (lead1, lead2) => lead2.createdOn.valueOf() - lead1.createdOn.valueOf(),
	},
	lastActiveOn: {
		columnType: LeadColumnType.LAST_ACTIVE_ON,
		permitted: (user) => !!user.permissions.leadLastActive,
		compare: (lead1, lead2) => {
			const lastActiveOn1 = lead1.lastActiveOn;
			const lastActiveOn2 = lead2.lastActiveOn;
			if (!lastActiveOn1 && !lastActiveOn2) return 0;
			if (!lastActiveOn1) return 1;
			if (!lastActiveOn2) return -1;
			return lastActiveOn2.valueOf() - lastActiveOn1.valueOf();
		},
	},
	lastSearchAgentChatOn: {
		columnType: LeadColumnType.LAST_SEARCH_AGENT_CHAT_ON,
		permitted: hasSearchAgent,
		isBlank: (lead) => !lead.lastSearchAgentChatOn,
		compare: (lead1, lead2) => {
			const lastSearchAgentChatOn1 = lead1.lastSearchAgentChatOn;
			const lastSearchAgentChatOn2 = lead2.lastSearchAgentChatOn;
			if (!lastSearchAgentChatOn1 && !lastSearchAgentChatOn2) return 0;
			if (!lastSearchAgentChatOn1) return 1;
			if (!lastSearchAgentChatOn2) return -1;
			// oldest first ascending, so the table's first click (descending) shows the newest chat first
			return lastSearchAgentChatOn1.valueOf() - lastSearchAgentChatOn2.valueOf();
		},
	},
	subscribed: {
		columnType: LeadColumnType.SUBSCRIBED,
		compare: (lead1, lead2) => (lead2.subscribed ? 1 : 0) - (lead1.subscribed ? 1 : 0),
	},
	rating: {
		columnType: LeadColumnType.RANKING,
		compare: (lead1, lead2) => ((lead1.ranking || 0) > (lead2.ranking || 0) ? -1 : 1),
	},
	source: {
		columnType: LeadColumnType.SOURCE,
		compare: (lead1, lead2) => {
			const source1 = lead1.source;
			const source2 = lead2.source;
			if (!source1 && !source2) return 0;
			if (!source1) return 1;
			if (!source2) return -1;
			return source1 < source2 ? -1 : 1;
		},
	},
	agent: {
		columnType: LeadColumnType.AGENT,
		permitted: (user) => !!(user.permissions.broker && user.permissions.showTeamFeatures),
		compare: (lead1, lead2) => {
			const agent1 = lead1.agent;
			const agent2 = lead2.agent;
			if (!agent1 && !agent2) return 0;
			if (!agent1) return 1;
			if (!agent2) return -1;
			return agent1.label.toLowerCase() < agent2.label.toLowerCase() ? -1 : 1;
		},
	},
};

// Matches the Lead List table, which only sorts on a column that is shown.
export const sortLeads = (leads: Lead[], sort: Sort, user: User, columnTypes: LeadColumnType[]) => {
	const sortColumn = leadSortColumns[sort.id];
	if (
		!sortColumn ||
		!columnTypes.includes(sortColumn.columnType) ||
		(sortColumn.permitted && !sortColumn.permitted(user))
	) {
		return leads;
	}
	return [...leads].sort(sortComparator(sortColumn.compare, sort.desc, sortColumn.isBlank));
};

export const getLeadsPageSortLabel = (sort: Sort) => {
	const sortColumn = leadSortColumns[sort.id];
	return sortColumn ? sortColumn.columnType.label : sort.id;
};
